/**
 * ArchetypeAnimation — looping SVG visualizations of each PG archetype's
 * play style. Used inside the Card 4 deck so each archetype card opens
 * with a distinctive visual signature before any text.
 *
 * Four kinds:
 *   "quarterback"     — perimeter passer who moves between spots. PG
 *                       slides to a new perimeter position, throws,
 *                       makes appear at the receiver. One pass at a
 *                       time, sequential.
 *   "downhill"        — paint attacker. PG starts on the wing, curves
 *                       past a defender en route to the rim, sprays out.
 *   "shotFirst"       — gravity creator. Defenders pulled toward PG,
 *                       passes out to now-open shooter, shot chart Xs
 *                       always visible.
 *   "floorArchitect"  — horizontal probing. Stays above the FT line,
 *                       weaves between wings, no rim approach. The
 *                       payoff is a pull-up middy or kick, not a layup.
 *
 * All animations loop forever. Hover the SVG to pause (so coaches can
 * read the labels). Pure CSS keyframes, no JS timer.
 */

import React from "react";
import { GOLD } from "../constants/theme";

const COURT = {
  // Half-court viewBox proportions: 500 wide x 470 tall.
  vb: "0 0 500 470",
  line: GOLD + "55",
  lineDim: GOLD + "30",
  bg: "#0A0A0A",
};

// Shared half-court SVG geometry.
function HalfCourt() {
  return (
    <g stroke={COURT.line} fill="none" strokeWidth={2}>
      {/* outer boundary */}
      <rect x="10" y="10" width="480" height="450"/>
      {/* three-point arc — center at the basket (250, 430), radius ~ 200 */}
      <path d={`M 50 460 L 50 360 A 200 200 0 0 1 450 360 L 450 460`}/>
      {/* free throw lane (paint) */}
      <rect x="180" y="290" width="140" height="170"/>
      {/* free throw line + circle */}
      <line x1="180" y1="290" x2="320" y2="290"/>
      <path d="M 180 290 A 70 70 0 0 0 320 290" />
      <path d="M 180 290 A 70 70 0 0 1 320 290" strokeDasharray="6 6"/>
      {/* backboard + rim */}
      <line x1="220" y1="445" x2="280" y2="445" strokeWidth={3}/>
      <circle cx="250" cy="430" r="9" stroke={GOLD + "88"} strokeWidth={2}/>
      {/* restricted-area arc */}
      <path d={`M 226 430 A 25 25 0 0 1 274 430`}/>
    </g>
  );
}

function Marker({ x, y, label, fill = GOLD, stroke = GOLD, r = 14, textColor = "#0A0A0A" }) {
  return (
    <g>
      <circle cx={x} cy={y} r={r} fill={fill} stroke={stroke} strokeWidth={2}/>
      <text x={x} y={y + 4} textAnchor="middle" fontSize="13" fontWeight="800"
        fill={textColor} fontFamily="'DM Sans', sans-serif">{label}</text>
    </g>
  );
}
function Defender({ x, y, label = "X" }) {
  return (
    <g>
      <circle cx={x} cy={y} r={11} fill="rgba(255,90,90,0.12)" stroke="#E0656A" strokeWidth={2}/>
      <text x={x} y={y + 4} textAnchor="middle" fontSize="11" fontWeight="800"
        fill="#E0656A" fontFamily="'DM Sans', sans-serif">{label}</text>
    </g>
  );
}

// ── 1. QUARTERBACK ────────────────────────────────────────────────────
// PG cycles through 4 perimeter spots. At each spot they pause briefly,
// throw one pass, the make-check pulses on the receiver, then the PG
// slides to the next spot. Strictly one pass on screen at a time.
//
// Cycle: 16s total, 4 passes of 4s each. Per pass:
//   0-15%  PG slides into position (silent)
//   15-50% pass arrow draws toward target receiver
//   50-75% green check pulses at the receiver
//   75-100% small hold before the next slide starts
function QuarterbackAnim() {
  // PG path through 4 perimeter spots, returning to start.
  // Top → Right Wing → Top → Left Wing → (back to Top via loop start)
  const pgPath = "M 250 140 L 380 200 L 250 140 L 120 200 L 250 140";

  return (
    <svg viewBox={COURT.vb} role="img" aria-label="Quarterback play visualization"
      style={{width: "100%", height: "100%", display: "block", background: COURT.bg}}>
      <HalfCourt/>

      <style>{`
        /* PG group moves along an offset-path between 4 perimeter spots */
        .qb-pg { offset-path: path('${pgPath}'); offset-rotate: 0deg;
                 animation: qbPgMove 16s ease-in-out infinite; }
        @keyframes qbPgMove {
          /* 4 spots equally spaced. Each "stay" phase is ~3.2s, with a
             ~0.8s slide between spots so the PG arrives before the pass. */
          0%, 18%   { offset-distance: 0%; }      /* Top of key */
          25%, 43%  { offset-distance: 25%; }     /* Right wing */
          50%, 68%  { offset-distance: 50%; }     /* Back at top */
          75%, 93%  { offset-distance: 75%; }     /* Left wing */
          100%      { offset-distance: 100%; }    /* Loop back to top */
        }

        .qb-pass { stroke: ${GOLD}; stroke-width: 3; fill: none; stroke-dasharray: 6 8;
                   stroke-linecap: round; opacity: 0; stroke-dashoffset: 200; }

        /* Each pass has its own keyframes so we can line up the firing
           window with the slice of the cycle where the PG is actually
           parked at the originating spot. PG schedule on the 16s loop:
             0%-18%   PG at TOP        (0.0s - 2.88s)
             18-25%   slide to R wing
             25-43%   PG at R WING     (4.0s - 6.88s)
             43-50%   slide back to top
             50-68%   PG at TOP        (8.0s - 10.88s)
             68-75%   slide to L wing
             75-93%   PG at L WING     (12.0s - 14.88s)
             93-100%  slide back to top
           Pass 1 fires while PG sits at top (first stop).
           Pass 2 fires while PG sits at right wing.
           Pass 3 fires while PG sits at top (second stop).
           Pass 4 fires while PG sits at left wing. */
        .qb-pass-1 { animation: qbPass1 16s ease-in-out infinite; }
        .qb-pass-2 { animation: qbPass2 16s ease-in-out infinite; }
        .qb-pass-3 { animation: qbPass3 16s ease-in-out infinite; }
        .qb-pass-4 { animation: qbPass4 16s ease-in-out infinite; }
        @keyframes qbPass1 {
          0%, 3%    { opacity: 0; stroke-dashoffset: 100; }
          7%        { opacity: 1; stroke-dashoffset: 50; }
          11%       { opacity: 1; stroke-dashoffset: 0; }
          15%       { opacity: 1; }
          17%, 100% { opacity: 0; }
        }
        @keyframes qbPass2 {
          0%, 28%   { opacity: 0; stroke-dashoffset: 100; }
          32%       { opacity: 1; stroke-dashoffset: 50; }
          36%       { opacity: 1; stroke-dashoffset: 0; }
          40%       { opacity: 1; }
          42%, 100% { opacity: 0; }
        }
        @keyframes qbPass3 {
          0%, 53%   { opacity: 0; stroke-dashoffset: 100; }
          57%       { opacity: 1; stroke-dashoffset: 50; }
          61%       { opacity: 1; stroke-dashoffset: 0; }
          65%       { opacity: 1; }
          67%, 100% { opacity: 0; }
        }
        @keyframes qbPass4 {
          0%, 78%   { opacity: 0; stroke-dashoffset: 100; }
          82%       { opacity: 1; stroke-dashoffset: 50; }
          86%       { opacity: 1; stroke-dashoffset: 0; }
          90%       { opacity: 1; }
          92%, 100% { opacity: 0; }
        }

        .qb-make { transform-origin: center; opacity: 0; }
        /* Make-check pulses just as its corresponding pass lands. */
        .qb-make-1 { animation: qbMake1 16s ease-in-out infinite; }
        .qb-make-2 { animation: qbMake2 16s ease-in-out infinite; }
        .qb-make-3 { animation: qbMake3 16s ease-in-out infinite; }
        .qb-make-4 { animation: qbMake4 16s ease-in-out infinite; }
        @keyframes qbMake1 {
          0%, 11%   { opacity: 0; transform: scale(0.5); }
          13%       { opacity: 1; transform: scale(1.3); }
          16%       { opacity: 1; transform: scale(1); }
          17%, 100% { opacity: 0; transform: scale(1); }
        }
        @keyframes qbMake2 {
          0%, 36%   { opacity: 0; transform: scale(0.5); }
          38%       { opacity: 1; transform: scale(1.3); }
          41%       { opacity: 1; transform: scale(1); }
          42%, 100% { opacity: 0; transform: scale(1); }
        }
        @keyframes qbMake3 {
          0%, 61%   { opacity: 0; transform: scale(0.5); }
          63%       { opacity: 1; transform: scale(1.3); }
          66%       { opacity: 1; transform: scale(1); }
          67%, 100% { opacity: 0; transform: scale(1); }
        }
        @keyframes qbMake4 {
          0%, 86%   { opacity: 0; transform: scale(0.5); }
          88%       { opacity: 1; transform: scale(1.3); }
          91%       { opacity: 1; transform: scale(1); }
          92%, 100% { opacity: 0; transform: scale(1); }
        }

        .qb-host:hover * { animation-play-state: paused; }
      `}</style>

      <g className="qb-host">
        {/* Static receivers always visible (they're the floor spacing) */}
        <Marker x={60}  y={350} label="C" r={12}/>{/* left corner */}
        <Marker x={440} y={350} label="C" r={12}/>{/* right corner */}
        <Marker x={90}  y={230} label="W" r={12}/>{/* left wing */}
        <Marker x={410} y={230} label="W" r={12}/>{/* right wing */}

        {/* Pass arrows — each one originates from the PG's spot at the
            time of that pass, ending at one of the static receivers.
            Pass 1 (PG at top)        → left wing
            Pass 2 (PG at right wing) → right corner
            Pass 3 (PG at top)        → right wing (variety)
            Pass 4 (PG at left wing)  → left corner (skip-ish) */}
        <path className="qb-pass qb-pass-1" d="M 250 140 Q 170 180, 90 230"/>
        <path className="qb-pass qb-pass-2" d="M 380 200 Q 420 280, 440 350"/>
        <path className="qb-pass qb-pass-3" d="M 250 140 Q 330 180, 410 230"/>
        <path className="qb-pass qb-pass-4" d="M 120 200 Q 90 280, 60 350"/>

        {/* Make checks — one per receiver corresponding to each pass */}
        <g className="qb-make qb-make-1" style={{transformOrigin: "90px 230px"}}>
          <circle cx="90" cy="230" r="14" fill="#5AB58422" stroke="#5AB584" strokeWidth={2}/>
          <path d="M 82 230 L 88 236 L 100 222" stroke="#5AB584" strokeWidth={3} fill="none" strokeLinecap="round" strokeLinejoin="round"/>
        </g>
        <g className="qb-make qb-make-2" style={{transformOrigin: "440px 350px"}}>
          <circle cx="440" cy="350" r="14" fill="#5AB58422" stroke="#5AB584" strokeWidth={2}/>
          <path d="M 432 350 L 438 356 L 450 342" stroke="#5AB584" strokeWidth={3} fill="none" strokeLinecap="round" strokeLinejoin="round"/>
        </g>
        <g className="qb-make qb-make-3" style={{transformOrigin: "410px 230px"}}>
          <circle cx="410" cy="230" r="14" fill="#5AB58422" stroke="#5AB584" strokeWidth={2}/>
          <path d="M 402 230 L 408 236 L 420 222" stroke="#5AB584" strokeWidth={3} fill="none" strokeLinecap="round" strokeLinejoin="round"/>
        </g>
        <g className="qb-make qb-make-4" style={{transformOrigin: "60px 350px"}}>
          <circle cx="60" cy="350" r="14" fill="#5AB58422" stroke="#5AB584" strokeWidth={2}/>
          <path d="M 52 350 L 58 356 L 70 342" stroke="#5AB584" strokeWidth={3} fill="none" strokeLinecap="round" strokeLinejoin="round"/>
        </g>

        {/* PG marker — slides along the offset-path between perimeter spots */}
        <g className="qb-pg">
          <circle cx="0" cy="0" r="14" fill={GOLD} stroke={GOLD} strokeWidth={2}/>
          <text x="0" y="4" textAnchor="middle" fontSize="13" fontWeight="800"
            fill="#0A0A0A" fontFamily="'DM Sans', sans-serif">PG</text>
        </g>
      </g>

      <text x="20" y="35" fontSize="13" fontWeight="800" fill={GOLD} letterSpacing="2"
        fontFamily="'Bebas Neue', sans-serif">QUARTERBACK · PERIMETER-FIRST CREATION</text>
    </svg>
  );
}

// ── 2. DOWNHILL ──────────────────────────────────────────────────────
// PG starts on the right wing, curves past the on-ball defender at the
// elbow, attacks the rim. Spray-out arrows fire after the drive collapses
// the help.
function DownhillAnim() {
  // Curved drive path: wing → split past defender → middle of the paint.
  // The PG stops in the heart of the paint (not at the rim). The story
  // is paint *pressure*, not a layup — the payoff is the kickout once
  // help collapses on them.
  const drivePath = "M 380 215 Q 320 270, 270 320 T 250 360";

  return (
    <svg viewBox={COURT.vb} role="img" aria-label="Downhill Creator play visualization"
      style={{width: "100%", height: "100%", display: "block", background: COURT.bg}}>
      <HalfCourt/>

      <style>{`
        /* Drive arrow draws in over the first half of the cycle */
        .dh-drive { stroke: ${GOLD}; stroke-width: 4; fill: none; stroke-linecap: round;
                    stroke-dasharray: 500; stroke-dashoffset: 500;
                    animation: dhDrive 7s ease-in-out infinite; }
        @keyframes dhDrive {
          0%   { stroke-dashoffset: 500; opacity: 0; }
          6%   { opacity: 1; }
          42%  { stroke-dashoffset: 0; opacity: 1; }
          70%  { stroke-dashoffset: 0; opacity: 1; }
          85%, 100% { opacity: 0; stroke-dashoffset: 0; }
        }

        /* PG moves along the same curve as the drive arrow */
        .dh-pg { offset-path: path('${drivePath}'); offset-rotate: 0deg;
                 animation: dhPg 7s ease-in-out infinite; }
        @keyframes dhPg {
          0%   { offset-distance: 0%; opacity: 1; }
          42%  { offset-distance: 100%; opacity: 1; }
          70%  { offset-distance: 100%; opacity: 1; }
          85%, 100% { offset-distance: 100%; opacity: 0; }
        }

        /* Spray-outs after the drive collapses the help */
        .dh-spray { stroke: ${GOLD}AA; stroke-width: 3; fill: none; stroke-dasharray: 4 8;
                    stroke-linecap: round; opacity: 0;
                    animation: dhSpray 7s ease-in-out infinite; }
        @keyframes dhSpray {
          0%, 45%   { opacity: 0; }
          55%       { opacity: 1; }
          75%, 100% { opacity: 0; }
        }

        /* Made-shot ring at the rim after the drive lands */
        .dh-make { animation: dhMake 7s ease-in-out infinite; opacity: 0; transform-origin: center; }
        @keyframes dhMake {
          0%, 55%  { opacity: 0; transform: scale(0.5); }
          65%      { opacity: 1; transform: scale(1.3); }
          80%      { opacity: 1; transform: scale(1); }
          100%     { opacity: 0; }
        }

        .dh-host:hover * { animation-play-state: paused; }
      `}</style>

      <g className="dh-host">
        {/* On-ball defender at the right elbow (the one PG splits past) */}
        <Defender x={340} y={290} label="X"/>
        {/* Help defender stepping over near the rim */}
        <Defender x={210} y={395} label="X"/>

        {/* Spray-out receivers */}
        <Marker x={60}  y={350} label="C" r={12}/>
        <Marker x={440} y={350} label="C" r={12}/>
        <Marker x={400} y={415} label="B" r={12} fill={GOLD+"66"}/>

        {/* Drive arrow (the curve past the defender) */}
        <path className="dh-drive" d={drivePath}/>

        {/* Spray-out arrows fire after the drive collapses the help */}
        <path className="dh-spray" d="M 250 360 Q 150 360, 60 350"/>
        <path className="dh-spray" d="M 250 360 Q 350 360, 440 350"/>
        <path className="dh-spray" d="M 250 360 Q 320 388, 400 415"/>

        {/* Made-shot ring on the right-corner shooter — the kickout
            becomes the bucket. PG never finished at the rim, the threat
            of pressure did the work. */}
        <g className="dh-make" style={{transformOrigin: "440px 350px"}}>
          <circle cx="440" cy="350" r="16" fill="#5AB58422" stroke="#5AB584" strokeWidth={2}/>
          <path d="M 430 350 L 437 357 L 451 341" stroke="#5AB584" strokeWidth={3} fill="none" strokeLinecap="round" strokeLinejoin="round"/>
        </g>

        {/* PG marker rides the drive path */}
        <g className="dh-pg">
          <circle cx="0" cy="0" r="14" fill={GOLD} stroke={GOLD} strokeWidth={2}/>
          <text x="0" y="4" textAnchor="middle" fontSize="13" fontWeight="800"
            fill="#0A0A0A" fontFamily="'DM Sans', sans-serif">PG</text>
        </g>
      </g>

      <text x="20" y="35" fontSize="13" fontWeight="800" fill={GOLD} letterSpacing="2"
        fontFamily="'Bebas Neue', sans-serif">DOWNHILL CREATOR · STRAIGHT-LINE PAINT PRESSURE</text>
    </svg>
  );
}

// ── 3. SHOT-FIRST ────────────────────────────────────────────────────
// Unchanged from prior pass per Coleman's "everything else looks pretty
// good" note. Gravity well + defenders pulling toward the PG + pass to
// the now-open shooter.
function ShotFirstAnim() {
  return (
    <svg viewBox={COURT.vb} role="img" aria-label="Shot-First play visualization"
      style={{width: "100%", height: "100%", display: "block", background: COURT.bg}}>
      <HalfCourt/>

      <style>{`
        .sf-gravity { animation: sfGravity 6s ease-in-out infinite; }
        @keyframes sfGravity {
          0%   { transform: translate(0, 0); }
          40%  { transform: translate(var(--tx, 0px), var(--ty, 0px)); }
          70%  { transform: translate(var(--tx, 0px), var(--ty, 0px)); }
          100% { transform: translate(0, 0); }
        }
        .sf-pulse { animation: sfPulse 6s ease-in-out infinite; transform-origin: 250px 140px; }
        @keyframes sfPulse {
          0%, 100% { r: 18; opacity: 0.0; }
          30%      { r: 60; opacity: 0.4; }
          50%      { r: 90; opacity: 0.0; }
        }
        .sf-shot { animation: sfShot 6s ease-in-out infinite; opacity: 0; }
        @keyframes sfShot {
          0%, 55% { opacity: 0; }
          70%     { opacity: 1; }
          90%     { opacity: 1; }
          100%    { opacity: 0; }
        }
        .sf-pass { stroke: ${GOLD}; stroke-width: 3; fill: none; stroke-dasharray: 4 8; stroke-linecap: round; opacity: 0;
                   animation: sfPass 6s ease-in-out infinite; }
        @keyframes sfPass {
          0%, 50%  { opacity: 0; stroke-dashoffset: 200; }
          60%      { opacity: 1; stroke-dashoffset: 0; }
          80%      { opacity: 1; }
          100%     { opacity: 0; }
        }
        .sf-pulse-host:hover * { animation-play-state: paused; }
      `}</style>

      <g className="sf-pulse-host">
        <circle className="sf-pulse" cx="250" cy="140" r="60" fill={GOLD} opacity="0"/>

        <Marker x={250} y={140} label="PG"/>

        <g className="sf-gravity" style={{"--tx":"30px","--ty":"-40px"}}>
          <Defender x={140} y={250} label="X"/>
        </g>
        <g className="sf-gravity" style={{"--tx":"-30px","--ty":"-40px"}}>
          <Defender x={360} y={250} label="X"/>
        </g>
        <g className="sf-gravity" style={{"--tx":"0px","--ty":"-30px"}}>
          <Defender x={250} y={300} label="X"/>
        </g>

        <Marker x={440} y={350} label="S" r={12}/>

        <path className="sf-pass" d="M 250 140 Q 360 220, 440 350"/>

        <g stroke={GOLD+"88"} strokeWidth={2.5} strokeLinecap="round">
          <line x1="245" y1="105" x2="255" y2="115"/>
          <line x1="255" y1="105" x2="245" y2="115"/>
          <line x1="155" y1="180" x2="165" y2="190"/>
          <line x1="165" y1="180" x2="155" y2="190"/>
          <line x1="335" y1="180" x2="345" y2="190"/>
          <line x1="345" y1="180" x2="335" y2="190"/>
        </g>

        <g className="sf-shot" style={{transformOrigin: "440px 350px"}}>
          <circle cx="440" cy="350" r="16" fill="#5AB58422" stroke="#5AB584" strokeWidth={2}/>
          <path d="M 432 350 L 438 356 L 450 342" stroke="#5AB584" strokeWidth={3} fill="none" strokeLinecap="round" strokeLinejoin="round"/>
        </g>
      </g>

      <text x="20" y="35" fontSize="13" fontWeight="800" fill={GOLD} letterSpacing="2"
        fontFamily="'Bebas Neue', sans-serif">SHOT-FIRST · GRAVITY OPENS THE FLOOR</text>
    </svg>
  );
}

// ── 4. FLOOR ARCHITECT ───────────────────────────────────────────────
// Horizontal weaving above the free throw line. Never approaches the
// rim. Defenders shift with each probe. Payoff is a mid-range pull-up
// at the elbow, not a layup.
function FloorArchitectAnim() {
  // Snaking path stays in y range 140-260 (well above the FT line at y=290).
  // Weaves between x=130 and x=380, with a slight dip at the elbow for the pull-up.
  const probePath = "M 250 140 Q 180 175, 130 210 Q 220 245, 320 220 Q 380 200, 380 175 Q 320 230, 250 250";

  // On-ball defender follows the same shape as the PG but shifted ~38px
  // closer to the basket so they always read as positioned between the
  // ball and the rim. They run with the same timing as the PG so they
  // visually shadow the move.
  const onballPath = "M 250 178 Q 180 213, 130 248 Q 220 283, 320 258 Q 380 238, 380 213 Q 320 268, 250 288";

  return (
    <svg viewBox={COURT.vb} role="img" aria-label="Floor Architect play visualization"
      style={{width: "100%", height: "100%", display: "block", background: COURT.bg}}>
      <HalfCourt/>

      <style>{`
        .fa-path { stroke: ${GOLD}; stroke-width: 3; fill: none; stroke-linecap: round;
                   stroke-dasharray: 900; stroke-dashoffset: 900;
                   animation: faPath 9s ease-in-out infinite; }
        @keyframes faPath {
          0%   { stroke-dashoffset: 900; opacity: 0; }
          5%   { opacity: 1; }
          75%  { stroke-dashoffset: 0; opacity: 1; }
          88%  { stroke-dashoffset: 0; opacity: 1; }
          100% { stroke-dashoffset: 0; opacity: 0; }
        }
        .fa-pg { offset-path: path('${probePath}'); offset-rotate: 0deg;
                 animation: faPg 9s ease-in-out infinite; }
        @keyframes faPg {
          0%   { offset-distance: 0%; opacity: 0; }
          5%   { opacity: 1; }
          75%  { offset-distance: 100%; opacity: 1; }
          88%  { offset-distance: 100%; opacity: 1; }
          100% { offset-distance: 100%; opacity: 0; }
        }

        /* On-ball defender — shadows the PG along a parallel path
           anchored closer to the basket. Same easing as the PG so the
           defender stays roughly between the ball and the rim the
           entire time. */
        .fa-onball { offset-path: path('${onballPath}'); offset-rotate: 0deg;
                     animation: faOnball 9s ease-in-out infinite; }
        @keyframes faOnball {
          0%   { offset-distance: 0%; opacity: 0; }
          5%   { opacity: 1; }
          75%  { offset-distance: 100%; opacity: 1; }
          88%  { offset-distance: 100%; opacity: 1; }
          100% { offset-distance: 100%; opacity: 0; }
        }

        /* Help defenders sit off-ball and slide toward the action as the
           PG probes each side. Two of them — one in the lane (nail/help
           the helper), one on the weakside elbow. They never leave their
           help zones, they just *react* to where the ball is. */
        .fa-help-1 { animation: faHelp1 9s ease-in-out infinite; }
        .fa-help-2 { animation: faHelp2 9s ease-in-out infinite; }
        @keyframes faHelp1 {
          /* Lane help — bumps with ball side. PG is left from ~14-30%,
             middle ~40-50%, right ~55-75%, drops to elbow ~80-95%. */
          0%   { transform: translate(0, 0); }
          14%  { transform: translate(-22px, 6px); }
          30%  { transform: translate(-28px, 10px); }
          50%  { transform: translate(0, 4px); }
          70%  { transform: translate(28px, 6px); }
          85%  { transform: translate(0, 22px); }
          100% { transform: translate(0, 0); }
        }
        @keyframes faHelp2 {
          /* Weakside help — mirrors ball side, opposite direction. */
          0%   { transform: translate(0, 0); }
          14%  { transform: translate(18px, -4px); }
          30%  { transform: translate(25px, -2px); }
          50%  { transform: translate(0, 0); }
          70%  { transform: translate(-30px, -4px); }
          85%  { transform: translate(-12px, 18px); }
          100% { transform: translate(0, 0); }
        }

        /* Pull-up indicator fires at the elbow at the END of the probe */
        .fa-pullup { animation: faPullup 9s ease-in-out infinite; opacity: 0; transform-origin: 250px 250px; }
        @keyframes faPullup {
          0%, 75%  { opacity: 0; transform: scale(0.5); }
          82%      { opacity: 1; transform: scale(1.3); }
          93%      { opacity: 1; transform: scale(1); }
          100%     { opacity: 0; }
        }
        .fa-host:hover * { animation-play-state: paused; }
      `}</style>

      <g className="fa-host">
        {/* Two help defenders — they live in help zones and shift with
            ball position. One nail/lane helper, one weakside elbow. */}
        <g className="fa-help-1">
          <Defender x={250} y={320} label="X"/>
        </g>
        <g className="fa-help-2">
          <Defender x={380} y={280} label="X"/>
        </g>

        {/* The horizontal probe path */}
        <path className="fa-path" d={probePath}/>

        {/* On-ball defender — shadows the PG along the parallel path */}
        <g className="fa-onball">
          <circle cx="0" cy="0" r="11" fill="rgba(255,90,90,0.12)" stroke="#E0656A" strokeWidth={2}/>
          <text x="0" y="4" textAnchor="middle" fontSize="11" fontWeight="800"
            fill="#E0656A" fontFamily="'DM Sans', sans-serif">X</text>
        </g>

        {/* PG slides along the probe path */}
        <g className="fa-pg">
          <circle cx="0" cy="0" r="14" fill={GOLD} stroke={GOLD} strokeWidth={2}/>
          <text x="0" y="4" textAnchor="middle" fontSize="13" fontWeight="800"
            fill="#0A0A0A" fontFamily="'DM Sans', sans-serif">PG</text>
        </g>

        {/* Final mid-range pull-up at the elbow (well above the paint) */}
        <g className="fa-pullup">
          <circle cx="250" cy="250" r="16" fill="#5AB58422" stroke="#5AB584" strokeWidth={2}/>
          <path d="M 241 250 L 248 257 L 261 242" stroke="#5AB584" strokeWidth={3} fill="none" strokeLinecap="round" strokeLinejoin="round"/>
        </g>
      </g>

      <text x="20" y="35" fontSize="13" fontWeight="800" fill={GOLD} letterSpacing="2"
        fontFamily="'Bebas Neue', sans-serif">FLOOR ARCHITECT · HORIZONTAL PROBE FOR THE SEAM</text>
    </svg>
  );
}

// ── Public component ────────────────────────────────────────────────
export function ArchetypeAnimation({ kind, C }) {
  return (
    <div style={{
      borderRadius: 12,
      overflow: "hidden",
      border: `1px solid ${(C && C.cardBorder) || "rgba(226,221,159,0.18)"}`,
      background: COURT.bg,
      aspectRatio: "500 / 470",
      marginBottom: 16,
      cursor: "pointer",
    }}
      title="Hover to pause">
      {kind === "quarterback"    && <QuarterbackAnim/>}
      {kind === "downhill"       && <DownhillAnim/>}
      {kind === "shotFirst"      && <ShotFirstAnim/>}
      {kind === "floorArchitect" && <FloorArchitectAnim/>}
    </div>
  );
}

export default ArchetypeAnimation;
